/**
 * Pill-style on/off switch used by the provider catalogs.
 *
 * Why a custom button instead of an `<input type="checkbox">`?
 * - We need a label that flips between "ON" / "OFF" inside the click
 *   target, which is awkward with native styling.
 * - We want a `pending` state — when the operator clicks while a save
 *   is in flight we visually pulse the thumb so they don't double-click
 *   thinking nothing happened.
 *
 * The thumb is positioned absolute relative to `.switch__track` (NOT
 * relative to the outer button). That matters because the button has
 * hover/focus padding so it has a comfortable hit target; positioning
 * the thumb on the button would push it out of the pill.
 */
import type { ReactNode } from "react";

interface ToggleSwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  /**
   * When true the switch is non-interactive but visible. Use for rows
   * that are inheriting from a parent scope, or while the operator is
   * still missing a credential.
   */
  disabled?: boolean;
  /**
   * When true the switch animates the thumb to indicate a request is in
   * flight. The click handler also short-circuits while pending so the
   * operator can't queue up duplicate writes.
   */
  pending?: boolean;
  /**
   * Optional override for the inline label. Defaults to "ON" / "OFF".
   * Pass a node (e.g. an `<X />` icon) to render arbitrary content.
   */
  onLabel?: ReactNode;
  offLabel?: ReactNode;
  /** ARIA label when there is no visible label outside the switch. */
  ariaLabel?: string;
}

export function ToggleSwitch({
  checked,
  onChange,
  disabled,
  pending,
  onLabel = "on",
  offLabel = "off",
  ariaLabel,
}: ToggleSwitchProps) {
  const className = [
    "switch",
    checked ? "is-on" : "",
    disabled ? "is-disabled" : "",
    pending ? "is-pending" : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      aria-busy={pending || undefined}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        if (pending) return;
        onChange(!checked);
      }}
      className={className}
    >
      <span className="switch__track">
        <span className="switch__thumb" />
      </span>
      <span className="switch__label">{checked ? onLabel : offLabel}</span>
    </button>
  );
}
